/**
 * Histogram — simple SVG bar chart over price/value bins. Shared between
 * the Market tab (price distribution per modelKey) and any future tab
 * that needs distribution-style visualisations.
 *
 * Each bin has `rangeStart`, `rangeEnd`, `count`. The component renders
 * one vertical bar per bin, scaled to the tallest bin = full height.
 *
 * Click handler optional — used by the Market tab to filter the trend
 * chart to a price band when a histogram bar is selected.
 */

import { cn } from '@/lib/cn';

export interface HistogramBin {
  rangeStart: number;
  rangeEnd: number;
  count: number;
}

interface Props {
  bins: HistogramBin[];
  /** Bar fill colour. */
  color?: string;
  /** Optional formatter for x-axis tick labels (default: '£N'). */
  formatX?: (n: number) => string;
  /** Optional click handler — passes the clicked bin. */
  onClick?: (bin: HistogramBin, index: number) => void;
  height?: number;
}

export default function Histogram({
  bins, color = '#34d399', formatX, onClick, height = 120,
}: Props) {
  if (!bins.length) {
    return <div className="text-xs text-slate-600">no data</div>;
  }
  const maxCount = Math.max(1, ...bins.map(b => b.count));
  const fmt = formatX ?? ((n: number) => `£${Math.round(n)}`);
  const w = 100;
  const barW = w / bins.length;

  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
      >
        {bins.map((b, i) => {
          const h = (b.count / maxCount) * (height - 2);
          const y = height - h - 1;
          return (
            <g key={i}>
              <rect
                x={i * barW + barW * 0.05}
                y={y}
                width={barW * 0.9}
                height={h}
                fill={color}
                fillOpacity={0.7}
                className={cn(onClick && 'cursor-pointer hover:opacity-100')}
                onClick={() => onClick?.(b, i)}
              >
                <title>{`${fmt(b.rangeStart)}–${fmt(b.rangeEnd)}: ${b.count}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      {/* x-axis ticks — first + last + midpoint */}
      <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-mono">
        <span>{fmt(bins[0].rangeStart)}</span>
        {bins.length > 2 && (
          <span>{fmt(bins[Math.floor(bins.length / 2)].rangeStart)}</span>
        )}
        <span>{fmt(bins[bins.length - 1].rangeEnd)}</span>
      </div>
    </div>
  );
}
